import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { QuoteSelector } from './quote-selector';
import { ModelSelector } from './model-selector';
import { useAiIntelligenceStore } from '../store/ai-intelligence-store';
import {
  Lightbulb,
  FileText,
  AlertTriangle,
  ShieldCheck,
  TrendingUp,
  PackagePlus,
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils/format';
import { cn } from '@/lib/utils/cn';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export function RecommendationsTab() {
  // Sugerencias
  const [sugQuoteId, setSugQuoteId] = useState('');
  const [sugModelId, setSugModelId] = useState('');

  // Justificación
  const [justQuoteId, setJustQuoteId] = useState('');
  const [justReason, setJustReason] = useState('');
  const [justCustomReason, setJustCustomReason] = useState('');
  const [justCompetitorPrice, setJustCompetitorPrice] = useState('');

  const {
    suggestions,
    isLoadingSuggestions,
    suggestionsError,
    fetchSuggestions,
    justification,
    isLoadingJustification,
    justificationError,
    fetchJustification,
  } = useAiIntelligenceStore();

  const handleFetchSuggestions = () => {
    if (!sugQuoteId && !sugModelId) return;
    fetchSuggestions({
      quoteId: sugQuoteId || undefined,
      modelId: sugModelId || undefined,
    });
  };

  const handleFetchJustification = () => {
    if (!justQuoteId) return;
    fetchJustification({
      quoteId: justQuoteId,
      reason: justReason || undefined,
      customReason: justCustomReason || undefined,
      competitorPrice: justCompetitorPrice ? parseFloat(justCompetitorPrice) : undefined,
    });
  };

  const priceRange = suggestions?.rangoPrecioSugerido;
  const priceChartData = priceRange
    ? [
        { name: 'Agresivo', precio: priceRange.agresivo, fill: '#f59e0b' },
        { name: 'Optimo', precio: priceRange.optimo, fill: '#10b981' },
        { name: 'Conservador', precio: priceRange.conservador, fill: '#3b82f6' },
        { name: 'Objetivo', precio: priceRange.objetivo, fill: '#8b5cf6' },
      ]
    : [];

  const reasonOptions = [
    { value: 'strategic_client', label: 'Cliente Estratégico' },
    { value: 'volume_deal', label: 'Volumen Alto' },
    { value: 'competitive_pressure', label: 'Presión Competitiva' },
    { value: 'relationship_building', label: 'Desarrollo de Relación' },
    { value: 'market_entry', label: 'Entrada a Mercado' },
    { value: 'renewal_retention', label: 'Retención/Renovación' },
    { value: 'custom', label: 'Razón Personalizada' },
  ];

  return (
    <div className="space-y-8">
      {/* ── Sección 1: Sugerencias de Precio ── */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-primary" />
            Sugerencias de Precio
          </h3>
          <p className="text-sm text-muted-foreground mt-1 ml-7">
            Analiza cotizaciones aprobadas anteriormente para sugerirte rangos de precio optimos.
            Muestra 4 niveles de precio (agresivo, optimo, conservador, objetivo), oportunidades de
            cross-sell con otros modelos, y alertas si el precio esta fuera de rango. Puedes buscar
            por cotización específica, por modelo, o ambos.
          </p>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <QuoteSelector
                value={sugQuoteId}
                onChange={setSugQuoteId}
                label="Cotización (opcional)"
              />
              <ModelSelector
                value={sugModelId}
                onChange={setSugModelId}
                label="Modelo (opcional)"
              />
            </div>
            <Button
              onClick={handleFetchSuggestions}
              disabled={(!sugQuoteId && !sugModelId) || isLoadingSuggestions}
            >
              {isLoadingSuggestions ? 'Analizando...' : 'Obtener Sugerencias'}
            </Button>
          </CardContent>
        </Card>

        {isLoadingSuggestions && <Skeleton className="h-64" />}

        {suggestionsError && (
          <Card className="border-destructive/50">
            <CardContent className="pt-6 flex items-center gap-3 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              <p>{suggestionsError}</p>
            </CardContent>
          </Card>
        )}

        {suggestions && !isLoadingSuggestions && (
          <div className="space-y-4">
            {/* Datos Históricos */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <StatCard
                label="Cotizaciones Analizadas"
                value={String(suggestions.datosHistoricos.cotizacionesAnalizadas)}
              />
              <StatCard
                label="Margen Promedio"
                value={suggestions.datosHistoricos.margenPromedio}
              />
              <StatCard label="Margen Mediano" value={suggestions.datosHistoricos.margenMediano} />
              <StatCard label="P25" value={suggestions.datosHistoricos.percentil25} />
              <StatCard label="P75" value={suggestions.datosHistoricos.percentil75} />
              <StatCard
                label="Dto. Promedio"
                value={suggestions.datosHistoricos.descuentoPromedio}
              />
            </div>

            {/* Rango de precios */}
            {priceChartData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Rango de Precio Sugerido</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={priceChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`}
                      />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={100} />
                      <Tooltip
                        formatter={(value) => [formatCurrency(Number(value)), 'Precio']}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                        }}
                      />
                      <Bar dataKey="precio" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                    {priceChartData.map((item) => (
                      <div key={item.name} className="text-center p-2 rounded bg-muted/20">
                        <p className="text-xs text-muted-foreground">{item.name}</p>
                        <p className="text-sm font-semibold">{formatCurrency(item.precio)}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Cross-sell */}
            {suggestions.crossSell && suggestions.crossSell.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <PackagePlus className="w-4 h-4" />
                    Oportunidades de Cross-Sell
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {suggestions.crossSell.map((cs, i) => (
                      <div
                        key={i}
                        className="flex justify-between items-center p-2 rounded bg-muted/20"
                      >
                        <span className="text-sm font-medium">{cs.modelo}</span>
                        <span className="text-xs text-muted-foreground">
                          {cs.vecesUsado} veces usado
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Alertas */}
            {suggestions.alertas && suggestions.alertas.length > 0 && (
              <div className="space-y-2">
                {suggestions.alertas.map((a, i) => (
                  <div
                    key={i}
                    className={cn(
                      'flex items-center gap-3 p-3 rounded-lg text-sm',
                      a.nivel === 'CRITICO' &&
                        'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
                      a.nivel === 'ALERTA' &&
                        'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
                      a.nivel === 'INFO' &&
                        'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                    )}
                  >
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {a.mensaje}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!suggestions && !isLoadingSuggestions && !suggestionsError && (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground py-8">
              <Lightbulb className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm max-w-md mx-auto">
                Selecciona al menos una cotización o un modelo arriba. El sistema analizara tus
                cotizaciones aprobadas para calcular rangos de precio y oportunidades.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Sección 2: Generador de Justificación ── */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Generador de Justificación de Ajuste
          </h3>
          <p className="text-sm text-muted-foreground mt-1 ml-7">
            Cuando necesitas pedir una excepcion de precio (descuento mayor al permitido, margen
            bajo el piso, etc.), esta herramienta genera un documento de justificacion estructurado
            con: analisis de precio y margen, historial del cliente, evaluacion de riesgo y una
            recomendación. Ideal para respaldar tu solicitud ante el aprobador.
          </p>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-4">
            <QuoteSelector value={justQuoteId} onChange={setJustQuoteId} />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Razón del ajuste</label>
                <SearchableSelect
                  value={justReason}
                  onValueChange={(val) => setJustReason(val)}
                  options={[
                    { value: '', label: 'Seleccionar...' },
                    ...reasonOptions.map((r) => ({ value: r.value, label: r.label })),
                  ]}
                  placeholder="Seleccionar..."
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Razón personalizada</label>
                <Input
                  placeholder="Detalle adicional..."
                  value={justCustomReason}
                  onChange={(e) => setJustCustomReason(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Opcional. Agrega contexto adicional a la justificación
                </p>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Precio competencia ($)</label>
                <Input
                  type="number"
                  placeholder="Ej: 45000"
                  value={justCompetitorPrice}
                  onChange={(e) => setJustCompetitorPrice(e.target.value)}
                  min={0}
                />
                <p className="text-xs text-muted-foreground">
                  Opcional. Si sabes el precio de un competidor, incluirlo fortalece la
                  justificacion
                </p>
              </div>
            </div>

            <Button
              onClick={handleFetchJustification}
              disabled={!justQuoteId || isLoadingJustification}
            >
              {isLoadingJustification ? 'Generando...' : 'Generar Justificación'}
            </Button>
          </CardContent>
        </Card>

        {isLoadingJustification && <Skeleton className="h-64" />}

        {justificationError && (
          <Card className="border-destructive/50">
            <CardContent className="pt-6 flex items-center gap-3 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              <p>{justificationError}</p>
            </CardContent>
          </Card>
        )}

        {justification && !isLoadingJustification && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{justification.encabezado}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Contexto */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <InfoItem label="Cotización" value={justification.contexto.cotizacion} />
                <InfoItem label="Cliente" value={justification.contexto.cliente} />
                <InfoItem label="Vendedor" value={justification.contexto.vendedor} />
                <InfoItem label="Modelo" value={justification.contexto.modelo} />
                <InfoItem label="Moneda" value={justification.contexto.moneda} />
              </div>

              {/* Análisis de Precio */}
              <div>
                <h4 className="text-sm font-semibold mb-2">Análisis de Precio</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <InfoItem
                    label="Costo Base"
                    value={formatCurrency(justification.analisisPrecio.costoBase)}
                  />
                  <InfoItem
                    label="Precio Final"
                    value={formatCurrency(justification.analisisPrecio.precioFinal)}
                  />
                  <InfoItem
                    label="Descuento"
                    value={justification.analisisPrecio.descuentoOtorgado}
                  />
                  <InfoItem label="Margen Real" value={justification.analisisPrecio.margenReal} />
                </div>
              </div>

              {/* Historial del Cliente */}
              <div>
                <h4 className="text-sm font-semibold mb-2">Historial del Cliente</h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <InfoItem
                    label="Total Cotizaciones"
                    value={String(justification.historialCliente.cotizacionesTotales)}
                  />
                  <InfoItem
                    label="Aprobadas"
                    value={String(justification.historialCliente.aprobadas)}
                  />
                  <InfoItem
                    label="Rechazadas"
                    value={String(justification.historialCliente.rechazadas)}
                  />
                  <InfoItem
                    label="Revenue Histórico"
                    value={formatCurrency(justification.historialCliente.revenueHistorico)}
                  />
                  <InfoItem label="Tasa Éxito" value={justification.historialCliente.tasaExito} />
                </div>
              </div>

              {/* Razón */}
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-sm font-medium mb-1">Razón: {justification.razon.tipo}</p>
                <p className="text-sm text-muted-foreground">{justification.razon.descripcion}</p>
              </div>

              {/* Evaluación de Riesgo */}
              <div
                className={cn(
                  'p-4 rounded-lg',
                  justification.evaluacionRiesgo.nivel === 'BAJO' &&
                    'bg-emerald-100 dark:bg-emerald-900/30',
                  justification.evaluacionRiesgo.nivel === 'MEDIO' &&
                    'bg-amber-100 dark:bg-amber-900/30',
                  justification.evaluacionRiesgo.nivel === 'ALTO' && 'bg-red-100 dark:bg-red-900/30'
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="w-4 h-4" />
                  <span className="font-semibold text-sm">
                    Riesgo: {justification.evaluacionRiesgo.nivel}
                  </span>
                </div>
                <p className="text-sm">{justification.evaluacionRiesgo.detalle}</p>
              </div>

              {/* Recomendación */}
              <div className="p-4 rounded-lg border-2 border-primary/20 bg-primary/5">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  <span className="font-semibold text-sm text-primary">Recomendación</span>
                </div>
                <p className="text-sm">{justification.recomendacion}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {!justification && !isLoadingJustification && !justificationError && (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground py-8">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm max-w-md mx-auto">
                Selecciona una cotización y opcionalmente una razón. Se generara un informe completo
                con análisis de precio, historial del cliente y evaluación de riesgo que puedes
                presentar al aprobador.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-2.5 rounded-lg border bg-card text-center">
      <p className="text-[10px] text-muted-foreground leading-tight mb-0.5">{label}</p>
      <p className="text-sm font-semibold">{value}</p>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-2 rounded bg-muted/20">
      <p className="text-[10px] text-muted-foreground">{label}</p>
      <p className="text-xs font-medium">{value}</p>
    </div>
  );
}
